import React, { useState } from 'react'
import { Link } from 'gatsby'
import styled, { keyframes } from 'styled-components'
import Modal from 'components/modal/modal'
import { useQuery } from '@tanstack/react-query'
import { H3, H4 } from 'components/new/typography'
import Button from 'components/button/button'
import { usePreferredLocation } from 'hooks/use-preferred-location'
import days from 'utils/constants/days'
import parseLocationHours from 'utils/location-hours/parse-location-hours'
import isSameDay from 'utils/dates/is-same-day'

export const LocationPickerModal = props => {
  const { modalProps } = props
  /**
   * @type {[string, import('react').Dispatch<string>]}
   */
  const [zip, setZip] = useState('')

  /**
   * @type {import('@tanstack/react-query').UseQueryResult<import('../api/nearby-locations').NearbyLocation[]>}
   */
  const { isFetching, isError, isSuccess, data } = useQuery({
    queryKey: ['nearby-locations', zip],
    staleTime: Infinity,
    queryFn: async () => {
      /**
       * @type {import('../api/nearby-locations').NearbyLocationsHandlerResponse}
       */
      const response = await fetch('/api/nearby-locations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          maxDistance: 100,
          zip,
        }),
      }).then(res => {
        if (!res.ok) {
          throw new Error('Network response was not okay')
        }
        return res.json()
      })

      if (response.error) {
        throw new Error(response.error)
      }

      return response.result
    },
    keepPreviousData: true,
    enabled: zip.length === 5,
  })

  const todayDate = new Date()

  const currentDayOfWeek = days[new Date().getDay()]

  const { preferredLocation, setPreferredLocation } = usePreferredLocation()

  const nearbyLocations = Array.isArray(data)
    ? data.map(location => {
        const isPreferred =
          preferredLocation && preferredLocation.locationNumber === location.locationNumber

        const todaySpecialHours = Array.isArray(location.specialHours)
          ? location.specialHours.find(specialHours => isSameDay(todayDate, new Date(specialHours.date)))
          : null

        const specialHoursTitle = todaySpecialHours ? todaySpecialHours.title : null

        const todayHours = todaySpecialHours
          ? todaySpecialHours.hours
          : location.hours[currentDayOfWeek]

        const todayHoursParsed = parseLocationHours(todayHours)

        const startDate =
          todayHoursParsed.closed === false
            ? new Date(
                todayDate.getFullYear(),
                todayDate.getMonth(),
                todayDate.getDate(),
                todayHoursParsed.start.slice(0, 2),
                todayHoursParsed.start.slice(3)
              )
            : null

        const endDate =
          todayHoursParsed.closed === false
            ? new Date(
                todayDate.getFullYear(),
                todayDate.getMonth(),
                todayDate.getDate(),
                todayHoursParsed.end.slice(0, 2),
                todayHoursParsed.end.slice(3)
              )
            : null

        // Parse hours, check if currently open
        const currentlyOpen = startDate < todayDate && endDate > todayDate

        const displayStatus =
          location.status === 'temporarily-closed'
            ? 'Temporarily Closed'
            : currentlyOpen
            ? 'Open'
            : 'Closed'

        const displayHours =
          location.status === 'temporarily-closed' || todayHoursParsed.closed ? null : todayHours

        return {
          ...location,
          currentlyOpen,
          displayStatus,
          displayHours,
          isPreferred,
          specialHoursTitle,
        }
      })
    : []

  /**
   * @type {React.ChangeEventHandler<HTMLInputElement>}
   */
  const handleZipCodeChange = e => {
    setZip(e.target.value)
  }
  return (
    <StyledModal {...modalProps}>
      <H3 as='p'>Find your nearest location</H3>
      <label htmlFor='location-picker-zip' hidden>
        Zip code
      </label>
      <input
        id='location-picker-zip'
        name='location-picker-zip'
        type='text'
        inputMode='numberic'
        placeholder='Search by zip code'
        pattern='^\d{5}$'
        maxLength={5}
        onChange={handleZipCodeChange}
        value={zip}
      />
      <div className='results-list'>
        {isFetching ? (
          <div className='loading-container'>
            <div className='loading-circle'></div>
          </div>
        ) : nearbyLocations.length > 0 ? (
          nearbyLocations.map(location => (
            <div className='result' key={location.locationNumber}>
              <p className='location-title'>{location.title}</p>
              <div className='location-details'>
                <div className='location-details-col-1'>
                  <p className='location-address'>
                    {location.address}
                    <br />
                    {location.city}, {location.state} {location.zip}
                  </p>
                  <p className='location-phone'>{location.phoneNumber}</p>
                </div>
                <div className='location-details-col-2'>
                  <p
                    className={`location-open-status ${
                      location.displayStatus === 'Open' ? 'open' : 'closed'
                    }`}
                  >
                    {location.displayStatus}
                  </p>
                  {location.displayHours ? (
                    <p className='location-open-hours'>{location.displayHours}</p>
                  ) : null}
                  {location.specialHoursTitle ? (
                    <p className='location-special-hours-title'>({location.specialHoursTitle})</p>
                  ) : null}
                  <p className='location-distance'>{location.distance} miles away</p>
                </div>
              </div>
              <div className='location-buttons'>
                <Button as={Link} to={location.slug} color='gray' size='small'>
                  View store details
                </Button>
                <Button
                  onClick={() => setPreferredLocation(location.locationNumber)}
                  disabled={location.isPreferred}
                  color='yellow'
                  size='small'
                >
                  {location.isPreferred ? 'My Store' : 'Set as My Store'}
                </Button>
              </div>
            </div>
          ))
        ) : isError ? (
          <div className='error'>
            <H4 as='p'>Something went wrong</H4>
            <p>
              We ran into a problem while trying to search for nearby locations. If problems persist,
              please contact our support team at{' '}
              <a href='contactus@hutsoninc.com'>contactus@hutsoninc.com</a>.
            </p>
          </div>
        ) : isSuccess ? (
          <p>No results found. Please try searching for another zip code.</p>
        ) : (
          <p>Search for a zip code to find nearby locations.</p>
        )}
      </div>
    </StyledModal>
  )
}

const spinAnimation = keyframes`
  100% {
    transform: rotate(360deg);
  }
`

const StyledModal = styled(Modal)`
  align-items: flex-start !important;
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;

  .loading-container {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  }

  .loading-circle {
    animation: ${spinAnimation} 0.67s infinite linear;
    border: 5px solid ${props => props.theme.color.n20};
    border-radius: 100%;
    border-top-color: ${props => props.theme.color.g400};
    box-sizing: border-box;
    height: 40px;
    width: 40px;
  }

  label {
    font-weight: bold;
  }

  input {
    flex-shrink: 0;
    width: 100%;
  }

  .results-list {
    flex-grow: 2;
    overflow-y: auto;
    margin: 16px 0;
    width: 100%;
  }

  .result {
    padding: 16px;
    border-top: 1px solid ${props => props.theme.color.n30};

    :last-child {
      border-bottom: 1px solid ${props => props.theme.color.n30};
    }

    p {
      color: ${props => props.theme.color.n900};
      font-size: 16px;
      line-height: 1.3;
    }

    .location-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 8px;
    }

    .location-details {
      display: flex;
      flex-direction: row;
    }

    .location-details-col-1 {
      flex-grow: 2;
    }

    .location-address {
      margin: 0 0 8px;
    }

    .location-phone {
      margin: 0;
    }

    .location-details-col-2 {
      text-align: right;
    }

    .location-open-status {
      font-size: 14px;
      font-weight: 700;
      margin: 0;

      &.open {
        color: ${props => props.theme.color.g400};
      }

      &.closed {
        color: ${props => props.theme.color.r500};
      }
    }

    .location-open-hours {
      margin: 4px 0 0;
    }

    .location-special-hours-title {
      color: ${props => props.theme.color.n400};
      font-size: 14px;
      margin: 0;
    }

    .location-distance {
      margin: 8px 0 0;
    }

    .location-buttons {
      margin-top: 16px;

      button,
      a {
        display: inline-block;
        margin-bottom: 8px;
        text-decoration: none;

        :not(:last-child) {
          margin-right: 8px;
        }
      }
    }
  }

  .error {
    background-color: ${props => props.theme.color.n10};
    padding: 24px;

    p {
      margin: 0;
    }
  }

  @media (min-width: 480px) {
    .result {
      .location-buttons {
        display: flex;

        flex-direction: row;

        button,
        a {
          margin-bottom: 0;

          :not(:last-child) {
            margin-right: 8px;
          }
        }
      }
    }
  }
`
